import React, { Component } from "react";
import PropTypes from "prop-types";
import Styled from "styled-components";
import { Container as MuiContainer } from "@material-ui/core";
import { withRouter } from "react-router-dom";
import { withTranslation } from "react-i18next";

import FormLogin from "./FormLogin";
import ForgotPassword from "./ForgotPassword";
import ResetPassword from "./ResetPassword";
import ResetSuccessDialog from "./ResetSuccessDialog";

import UserService from "../../services/UserService";
import AppService from "../../services/AppService";
import { passwordPolicyErrors } from "../../utils/passwordPolicy";
import { mapResetPasswordError, resetErrorField } from "../../utils/passwordFlowErrors";

const Background = Styled.div`
  min-height: 100vh;
  width: 100%;
  background-color: #ddd;
  background-image: url(${({ theme }) => theme.images.loginBackground});
  background-size: cover;
  background-position: center;
`;

const Fade = Styled.div`
  width: 100%;
  min-height: 100vh;
  background-color: ${({ theme }) => theme.color.background.login};
  padding: 20px;
  box-sizing: border-box;
`;

const Logo = Styled.div`
  background-image: url(${({ theme }) => theme.images.logo});
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
  height: 27vh;
  max-width: 290px;
  margin: 0 auto;
  position: sticky;
  z-index: 10;
`;

const Container = Styled(MuiContainer)`
  box-sizing: content-box;
  max-width: 640px;
  padding-bottom: 20px;
`;

const Paper = Styled.div`
  position: relative;
  background-color: #fff;
  border-radius: 8px;
  margin-top: 20px;
  padding: 44px 74px ${({ $expanded }) => ($expanded ? "56px" : "40px")};
  min-height: 426px;
  height: ${({ $expanded }) => ($expanded ? "auto" : "426px")};
  box-sizing: border-box;

  .main-button {
    max-width: 300px;
    margin: ${({ $expanded }) => ($expanded ? "24px auto 0" : "0 auto")};
    display: block;
    position: ${({ $expanded }) => ($expanded ? "relative" : "absolute")};
    bottom: ${({ $expanded }) => ($expanded ? "auto" : "40px")};
    left: 0;
    right: 0;
  }
`;

const AppVersion = Styled.span`
  font-size: 16px;
  font-weight: 300;
  color: rgba(0,0,0,.5);
  position: absolute;
  bottom: 12px;
  right: 12px;
`;

class Login extends Component {
  constructor(props) {
    super(props);
    this.state = {
      email: "",
      password: "",
      loading: false,
      view: "login",
      forgotPasswordSupport: { phone: "", email: "" },
      allowEmailLogin: false,
      identifier: "",
      token: "",
      newPassword: "",
      confirmationPassword: "",
      formError: "",
      formErrorField: "",
      resetSuccessOpen: false,
      errors: {
        email: "",
        password: ""
      }
    };
  }

  componentDidMount() {
    this.getForgotPasswordSupport();
    this.getEmployeeLoginOptions();
  }

  validateForm = async () => {
    // const { email } = this.state;
    // if (!(await this.validateEmail(email))) {
    //   throw new Error("Email inválido");
    // }
  };

  validateEmail = async email => {
    const cleanEmail = email && email.replace("#backdoor", "");
    const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return re.test(String(cleanEmail).toLowerCase());
  };

  openForgotPassword = () => {
    this.setState(prev => ({
      view: "forgot",
      identifier: prev.email || prev.identifier,
      formError: "",
      formErrorField: ""
    }));
  };

  closeForgotPassword = () => {
    this.setState({
      view: "login",
      formError: "",
      formErrorField: "",
      loading: false
    });
  };

  openResetPassword = () => {
    this.setState(prev => ({
      view: "reset",
      identifier: prev.identifier || prev.email,
      formError: "",
      formErrorField: "",
      loading: false
    }));
  };

  handleFieldChange = prop => event => {
    this.setState({ [prop]: event.target.value, formError: "", formErrorField: "" });
  };

  getForgotPasswordSupport = async () => {
    try {
      const res = await AppService.getForgotPasswordSupport();
      this.setState({
        forgotPasswordSupport: {
          phone: (res && res.phone) || "",
          email: (res && res.email) || ""
        }
      });
    } catch (e) {
      console.error(e);
    }
  };

  getEmployeeLoginOptions = async () => {
    try {
      const res = await AppService.getEmployeeLoginOptions();
      this.setState({ allowEmailLogin: !!(res && res.allowEmail) });
    } catch (e) {
      console.error(e);
    }
  };

  handleSubmit = async () => {
    const { email, password } = this.state;
    const { getCampuses } = this.props;
    this.setState({ loading: true });

    try {
      await this.validateForm();
      await UserService.login(email, password);
      window.location.replace("/");
      getCampuses();
    } catch (err) {
      const { message } = err;
      const { t } = this.props;
      if (message === "Senha incorreta") {
        this.setState({
          errors: { password: t("login.error.wrongPassword"), email: "" }
        });
      } else {
        this.setState({ errors: { password: "", email: message } });
      }
      this.setState({ loading: false });
    }
  };

  handleForgotSubmit = async () => {
    const { identifier, email } = this.state;
    const { t } = this.props;
    const value = (identifier || email || "").trim();
    if (!value) {
      this.setState({ formError: t("login.identifierRequired") });
      return;
    }

    this.setState({ loading: true, formError: "", formErrorField: "" });
    try {
      await UserService.forgotPassword(value);
      this.setState({
        loading: false,
        view: "reset",
        identifier: value
      });
    } catch (err) {
      this.setState({
        loading: false,
        formError: (err && err.message) || t("login.forgotError"),
        formErrorField: ""
      });
    }
  };

  handleResetSubmit = async () => {
    const { identifier, token, newPassword, confirmationPassword } = this.state;
    const { t } = this.props;

    if (!(identifier || "").trim() || !(token || "").trim()) {
      this.setState({
        formError: t("login.resetRequired"),
        formErrorField: !(token || "").trim() ? "token" : ""
      });
      return;
    }
    if (newPassword !== confirmationPassword) {
      this.setState({
        formError: t("login.confirmMismatch"),
        formErrorField: "confirmationPassword"
      });
      return;
    }
    const policy = passwordPolicyErrors(newPassword, t);
    if (policy.length) {
      this.setState({ formError: policy[0], formErrorField: "" });
      return;
    }

    this.setState({ loading: true, formError: "", formErrorField: "" });
    try {
      await UserService.resetPassword({
        identifier: identifier.trim(),
        token: token.trim(),
        newPassword,
        confirmationPassword
      });
      this.setState({
        loading: false,
        view: "login",
        password: "",
        token: "",
        newPassword: "",
        confirmationPassword: "",
        resetSuccessOpen: true,
        email: identifier.trim()
      });
    } catch (err) {
      const formError = mapResetPasswordError(err && err.message, t);
      this.setState({
        loading: false,
        formError,
        formErrorField: resetErrorField(formError, t)
      });
    }
  };

  closeResetSuccess = () => {
    this.setState({ resetSuccessOpen: false });
  };

  renderView() {
    const {
      email,
      password,
      view,
      loading,
      errors,
      forgotPasswordSupport,
      allowEmailLogin,
      identifier,
      token,
      newPassword,
      confirmationPassword,
      formError,
      formErrorField
    } = this.state;

    if (view === "forgot") {
      return (
        <ForgotPassword
          closeForgotPassword={this.closeForgotPassword}
          supportPhone={forgotPasswordSupport.phone}
          supportEmail={forgotPasswordSupport.email}
          identifier={identifier}
          onChangeIdentifier={this.handleFieldChange("identifier")}
          onSubmit={this.handleForgotSubmit}
          loading={loading}
          error={formError}
          onGoToReset={this.openResetPassword}
        />
      );
    }

    if (view === "reset") {
      return (
        <ResetPassword
          closeResetPassword={this.openForgotPassword}
          identifier={identifier}
          token={token}
          newPassword={newPassword}
          confirmationPassword={confirmationPassword}
          onChange={this.handleFieldChange}
          onSubmit={this.handleResetSubmit}
          loading={loading}
          error={formError}
          errorField={formErrorField}
          supportPhone={forgotPasswordSupport.phone}
          supportEmail={forgotPasswordSupport.email}
        />
      );
    }

    return (
      <FormLogin
        onSubmit={this.handleSubmit}
        handleChange={this.handleFieldChange}
        email={email}
        password={password}
        openForgotPassword={this.openForgotPassword}
        loading={loading}
        errors={errors}
        allowEmailLogin={allowEmailLogin}
      />
    );
  }

  render() {
    const { view, resetSuccessOpen } = this.state;
    const expanded = view !== "login";

    return (
      <Background>
        <Fade>
          <Logo />
          <Container component="main">
            <Paper $expanded={expanded}>
              {this.renderView()}
              <AppVersion>{process.env.REACT_APP_VERSION}</AppVersion>
            </Paper>
          </Container>
          <ResetSuccessDialog
            open={resetSuccessOpen}
            onClose={this.closeResetSuccess}
          />
        </Fade>
      </Background>
    );
  }
}

Login.propTypes = {
  getCampuses: PropTypes.func.isRequired,
  history: PropTypes.shape({
    push: PropTypes.func
  }).isRequired,
  t: PropTypes.func.isRequired
};

export default withRouter(withTranslation()(Login));
